
import { computed, defineComponent, onBeforeMount, PropType, reactive } from 'vue'

// components
import TheButton from '@/common/components/UI/TheButton/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'
import OrderCardStatusesLine from '@/modules/NewDispatcher/components/OrderCardStatusesLine/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import CostFiles from '@/modules/InvoiceModule/components/CostFiles/index.vue'
import ShortcomingFiles from '@/modules/InvoiceModule/components/ShortcomingFiles/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'
import NewTable from '@/common/new-components/NewTable/index.vue'
import NewModal from '@/common/new-components/NewModal/index.vue'
import InvoiceStatusRow from '@/modules/InvoiceModule/components/InvoiceStatusRow/index.vue'

// types
import { ScheduleT } from '@/core/types/dispatcher.types'
import { TakeOrderFull } from '@/new-services/types/takes'
import { CompanySchedule } from '@/new-services/types/companies'

// realisations
import takeAPI from '@/new-services/takes.service'
import companyAPI from '@/new-services/companies.service'

const WEEK_DAYS = ['ВС', 'ПН', 'ВТ', 'СР', 'ЧТ', 'ПТ', 'СБ']
type WaitListStatus = {
    [key: number]: string
}
const WAIT_LIST_STATUSES: WaitListStatus = {
    48: 'green',
    47: 'yellow',
    49: 'red',
}
import { OKTELL_WAITLIST_STATUSES_WITH_DATE } from '@/core/constants/oktell.constants'

const COLUMNS = [
    {
        key: 'createdAt',
        name: 'Дата создания',
    },
    {
        key: 'status',
        name: 'Статус',
    },
    {
        key: 'state',
        name: 'Состояние',
    },
    {
        key: 'user',
        name: 'Кто проставил',
    },
    {
        key: 'clientType',
        name: 'Когда проставил',
    },
    {
        key: 'comment',
        name: 'Комментарий',
    },
    {
        key: 'source',
        name: 'Источник создания',
    },
]

export default defineComponent({
    components: {
        TheButton,
        OrderCardStatusesLine,
        Cheap,
        CostFiles,
        Loader,
        ShortcomingFiles,
        NewButton,
        NewTable,
        NewModal,
        InvoiceStatusRow,
    },
    props: {
        orderId: {
            type: Number,
            default: null,
        },
        companyId: {
            type: Number,
            default: null,
        },
        hasParkingFiles: {
            type: Boolean,
            default: false,
        },
        hasRoadFiles: {
            type: Boolean,
            default: false,
        },
        problems: {
            type: Array as PropType<string[]>,
            default: () => [],
        },
    },
    emits: [
        'start-submit-order',
        'refresh',
        'date-changed',
        'assign-order',
        'change-date',
        'wait-list',
        'shortcoming',
        'payment',
    ],
    setup(props, { emit }) {
        const state_lo = reactive({
            is_loading: false,
            is_open: false,
        })

        const emitStartSubmitOrder = () => {
            emit('start-submit-order')
        }

        const emitRefresh = () => {
            emit('refresh')
        }

        const state = reactive({
            is_changing_date: false,
            is_loading: false,
            for_all_invoices: 0,
            //
            take_order: null as TakeOrderFull | null,
            company_schedule: [] as CompanySchedule[],
        })

        const getAdditionalInfo = computed(() => {
            let services: string[] = []

            if (state.take_order && state.take_order.orderAdditionalServicesValues) {
                state.take_order.orderAdditionalServicesValues.forEach((service) => {
                    services.push(service.typeName)
                })
            }

            return services.join(', ')
        })

        const getScheduleStr = (schedule: ScheduleT) => {
            if (schedule)
                return `${WEEK_DAYS[schedule.weekday]}: ${schedule.work_time_from} - ${
                    schedule.work_time_until
                }`

            return ''
        }

        const getScheduleStrFinal = (schedules: ScheduleT[]) => {
            let result: string[] = []
            let schedules_filtered: ScheduleT[] = []

            schedules.forEach((schedule) => {
                let schedule_found = schedules_filtered.find(
                    (s) =>
                        s.work_time_from === schedule.work_time_from &&
                        s.work_time_until === schedule.work_time_until
                )

                if (!schedules_filtered.length || !schedule_found) {
                    schedules_filtered.push(schedule)
                    result.push(getScheduleStr(schedule))
                } else {
                    let index_found = schedules_filtered.indexOf(schedule_found)

                    let str_arr = result[index_found].split(': ')
                    let new_str = `${str_arr[0].concat(`, ${WEEK_DAYS[schedule.weekday]}`)}`

                    result[index_found] = `${new_str}: ${str_arr[1]}`
                }
            })

            return result
        }

        const loadOrderTake = async () => {
            if (!props.orderId) return

            try {
                state.is_loading = true

                const response = await takeAPI.getTakeOrder(props.orderId)
                state.take_order = response.data.data
            } catch (error) {
                console.error(error)
            } finally {
                state.is_loading = false
            }
        }

        const loadCompanySchedule = async () => {
            if (!props.companyId) return

            try {
                state.is_loading = true

                const response = await companyAPI.getCompanySchedule(props.companyId)
                state.company_schedule = response.data.data
            } catch (error) {
                console.error(error)
            } finally {
                state.is_loading = false
            }
        }

        const emitAssignOrder = () => {
            emit('assign-order', { ...state.take_order })
        }

        const emitChangeDate = () => {
            emit('change-date', { ...state.take_order })
        }

        const emitWaitList = () => {
            emit('wait-list', { ...state.take_order })
        }

        const emitShortcoming = () => emit('shortcoming', { ...state.take_order })
        const emitPayment = (type: number) => emit('payment', type, { ...state.take_order })

        // computed

        const schedule_take = computed(() =>
            state.company_schedule.filter((weekday) => weekday.scheduleTypeId === 15)
        )

        const schedule_delivery = computed(() =>
            state.company_schedule.filter((weekday) => weekday.scheduleTypeId === 16)
        )

        const getWaitListClass = computed(() => {
            const classes = ['primary', 'small', 'mt-2']

            if (state.take_order && state.take_order.waitList && state.take_order.waitList.state) {
                const state_id = state.take_order.waitList.state.id
                if (state_id) {
                    classes.push(WAIT_LIST_STATUSES[state_id])
                }
            }

            return classes.join(' ')
        })

        const getWaitListStatus = computed(() => {
            if (
                !state.take_order ||
                !state.take_order.waitList ||
                !state.take_order.waitList.status
            ) {
                return ''
            }

            const { name, code } = state.take_order.waitList.status
            const { value } = state.take_order.waitList

            let status_name = name

            if (OKTELL_WAITLIST_STATUSES_WITH_DATE.includes(code)) {
                status_name += ` (${value})`
            }

            return `${status_name}`
        })

        const toggleLo = () => (state_lo.is_open = !state_lo.is_open)

        const isLoModal = computed(() => {
            return (
                state_lo.is_open &&
                state.take_order &&
                state.take_order.waitListStatusesHistory &&
                state.take_order.waitListStatusesHistory.length
            )
        })

        //

        onBeforeMount(() => {
            loadOrderTake()
            loadCompanySchedule()
        })

        //

        return {
            state,
            state_lo,
            //
            emitStartSubmitOrder,
            emitRefresh,
            getAdditionalInfo,
            getScheduleStrFinal,
            // computed
            schedule_take,
            schedule_delivery,
            emitAssignOrder,
            emitChangeDate,
            emitWaitList,
            emitShortcoming,
            emitPayment,
            getWaitListClass,
            getWaitListStatus,
            COLUMNS,
            toggleLo,
            isLoModal,
        }
    },
})
